import remove from 'lodash/remove';
import { Scene } from '@babylonjs/core/scene';

import { SystemQuality } from './systemQuality';
import { SystemEvent } from './system';
import { Animation } from './Animation/animation';

export class SystemAnimation extends SystemQuality {
    public fps = 60;

    private fpsratio = 1;

    private focusback = false;

    private frameBeforeEnd = 0;

    private frameSinceStarted = 0;

    private list: Animation[] = [];

    /**
    * List of all process which need rendering
    * Allow to have engine stop if nothing need rendering
    * Thus improving performance
    */

    constructor(canvas: HTMLCanvasElement) {
        super(canvas);
        window.addEventListener('focus', () => {
            this.setFocusBack();
        });

        window.addEventListener('blur', () => {
            this.setFocusBack();
        });

        window.addEventListener('scroll', () => {
            this.checkStartRender();
        });

        window.addEventListener('focus', () => {
            this.checkStartRender();
            // When focus on page after launchedRender called we need to restart the check
            if (!this.launched && this.isCheckingReady) {
                this.launchRender(this.currentLaunchCallback);
            }
        });

        this.on(SystemEvent.Resize, () => {
            if (this.launched) this.scene.render();
        });

        this.setCheckStartLoop();
    }

    private startCheckInterval = 1000;

    private setCheckStartLoop() {
        this.checkStartRender();
        setInterval(() => {
            if (!this.launched) this.checkStartRender();
            if (this.rendering) this.checkHardwareScaling();
            // This stops rendering if canvas not visible anymore
            this.checkVisible();
        }, this.startCheckInterval);
    }

    /**
    * Tell if system currently rendering scene
    */
    public rendering = false;

    /**
    * Tell if scene needs to be render
    */
    public launched = false;

    /**
     * @ignore
     */
    public checkStartRender(): void {
        if (this.launched) {
            // If overflow style = hidden, there is no scrollingElement on document
            this.checkSizeChange();
            const containerVisible = this.checkVisible();
            if (containerVisible && (this.list.length !== 0 || !this.needProcess)) {
                this.startRender();
            } else if (this.needProcess) {
                this.pauseRender();
            }
        }
    }

    /**
     * Allow to launch scene rendering (when everything is loaded for instance)
     */
    private currentLaunchCallback: () => void;

    public launchRender(callback?: () => void): void {
        this.currentLaunchCallback = callback;
        this.checkSceneReadyToRender(() => {
            this.launched = true;
            this.checkStartRender();
            if (callback) callback();
        });
    }

    /**
     * Stop scene rendering
     */
    public stopRender(): void {
        // console.log('stop render');
        this.launched = false;
        this.pauseRender();
    }

    /**
     * @ignore
     */
    public pauseRender(): void {
        if (!this.rendering) return;
        // console.log('stop');
        this.notify(SystemEvent.Stop, 0);
        this.rendering = false;
        this.engine.stopRenderLoop();
        this.engine.runRenderLoop(() => { });
    }

    /**
     * @ignore
     */
    public startRender(): void {
        if (this.rendering || !this.launched) return;
        // console.log('start');
        this.rendering = true;
        this.engine.resize();
        this.forceRender();
    }

    protected forceRender(): void {
        this.frameSinceStarted = 0;
        this.notify(SystemEvent.Start, 0);
        this.engine.stopRenderLoop();
        this.renderLoop(this.scene);
    }

    public renderLoop(scene: Scene): void {
        if (this.limitFPS) {
            this.engine.runRenderLoop(() => {
                const stopped = this.checkStoppedAnimations();
                this.runAnimations();
                if ((this.limitSwitch && this.rendering) || stopped) {
                    scene.render();
                }
                this.limitSwitch = !this.limitSwitch;
                this.removeStoppedAnimations();
            });
        } else {
            this.engine.runRenderLoop(() => {
                this.checkStoppedAnimations();
                this.runAnimations();
                scene.render();
                this.removeStoppedAnimations();
            });
        }
    }

    public isRendering(): boolean {
        return this.rendering;
    }

    /**
     * Make one step forward for all animations
     * @param fps Frame per second of the engine
     */
    private runAnimations() {
        this.fps = this.engine.getFps();
        this.fpsratio = 60 / this.fps;

        this.frameBeforeEnd = 0;
        // To avoid acceleration when focus back
        // if (this.focusback) return;
        const fpsratio = Math.min(this.fpsratio, 2);
        this.list.forEach((anim) => {
            if (anim.running) {
                anim.run(anim.count);
                anim.count += anim.step * fpsratio;
                if (anim.howmany - anim.count > this.frameBeforeEnd) {
                    this.frameBeforeEnd = Math.round(anim.howmany - anim.count + 1);
                }
            }
        });

        // We avoid sending start and end at the same time
        this.frameSinceStarted++;
        if (this.frameBeforeEnd < this.lastFrameNumberCheck) {
            this.notify(SystemEvent.End, this.frameBeforeEnd);
        } else if (this.frameSinceStarted < this.firstFrameNumberCheck) {
            this.notify(SystemEvent.Begin, this.frameSinceStarted);
        }
    }

    private checkStoppedAnimations(): boolean {
        let animStopped = false;
        this.list.forEach((anim) => {
            if (anim.running) {
                if (anim.count >= anim.howmany) {
                    animStopped = true;
                    anim.stop(true);
                    // this.removeAnimation(anim);
                }
            } else {
                // this.removeAnimation(anim);
            }
        });
        return animStopped;
    }

    private removeStoppedAnimations(): boolean {
        const animStopped = false;
        this.list.forEach((anim) => {
            if (!anim.running) {
                this.removeAnimation(anim);
            }
        });
        return animStopped;
    }

    private lastFrameNumberCheck = 20;

    private firstFrameNumberCheck = 2;

    /**
     * Make a small pause of animations (Used when focus is back to window)
     */
    private setFocusBack() {
        this.focusback = true;
        // In case in worker
        // if (localStorage) localStorage.clear();
        setTimeout(() => {
            this.focusback = false;
        }, 200);
    }

    /**
    * Add a rendering process
    */
    public addAnimation(animation: Animation): void {
        // console.log(this.list.indexOf(animation) === -1, animation.key);
        if (this.list.indexOf(animation) === -1) {
            // console.log('add', animation);
            this.list.push(animation);
            this.checkStartRender();
        }
    }

    /**
    * @ignore
    */
    private needProcess = true;

    /**
    * @ignore
    */
    public setNeedProcess(needProcess: boolean): void {
        this.needProcess = needProcess;
        if (!needProcess && this.launched) this.startRender();
    }

    protected limitFPS = false;

    // Keep first value as true so that render function is called straight away
    // Otherwise you could have a flash
    protected limitSwitch = true;

    public setLimitFPS(limitFPS: boolean): void {
        if (limitFPS === this.limitFPS) return;
        this.limitFPS = limitFPS;
        if (this.rendering) this.forceRender();
    }

    /**
    * Remove a rendering process
    */
    private removeAnimation(animation: Animation) {
        remove(this.list, (a: Animation) => a.key === animation.key);
        // console.log('remove', animation);
        this.checkStopRender();
    }

    /**
    * Check if there is still a process which need renderong
    */
    public checkStopRender(): void {
        // console.log(this.list.length, this.needProcess);
        if (this.list.length === 0 && this.needProcess) this.pauseRender();
    }

    /**
    * Make a quick render in order to update the scene
    */
    public quickRender(time?: number): void {
        this.startRender();
        setTimeout(() => {
            this.checkStopRender();
        }, time || 20);
    }

    public dispose(): void {
        this.stopRender();
        this.disposeScene();
    }
}
